import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiFileText } from 'react-icons/fi';
import { CHART_FONT, CHART_TOOLTIP } from 'helpers/echartsTheme';

const STAGE_COLORS = {
  quotation:            { bg: '#EDE9FE', text: '#5B21B6', bar: '#8B5CF6', dot: '#8B5CF6' },
  technical_definition: { bg: '#FEF3C7', text: '#92400E', bar: '#F59E0B', dot: '#F59E0B' },
  concept_design:       { bg: '#DBEAFE', text: '#1E40AF', bar: '#3B82F6', dot: '#3B82F6' },
  proposal_submission:  { bg: '#D1FAE5', text: '#065F46', bar: '#10B981', dot: '#10B981' }
};

const DEFAULT_DOT = '#6366F1';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const DonutSlice = ({ data }) => {
  const total = data.reduce((s, d) => s + d.count, 0);

  const option = total === 0
    ? {
        textStyle: { fontFamily: CHART_FONT.fontFamily },
        graphic: [{
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: 'No data', fontSize: 12, fill: '#9CA3AF', fontFamily: CHART_FONT.fontFamily }
        }],
        series: [{
          type: 'pie',
          radius: ['52%', '82%'],
          silent: true,
          label: { show: false },
          labelLine: { show: false },
          data: [{ value: 1, itemStyle: { color: '#F3F4F6' } }]
        }]
      }
    : {
        textStyle: { fontFamily: CHART_FONT.fontFamily },
        tooltip: {
          ...CHART_TOOLTIP,
          trigger: 'item',
          formatter: (params) => {
            const item = data[params.dataIndex];
            if (!item) return '';
            const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
            return [
              `<div style="font-weight:700;color:#111827">${item.label}</div>`,
              `<div style="font-size:12px;color:#374151">${item.count} quotations (${pct}%)</div>`,
              `<div style="font-size:11px;color:#6b7280">${fmtM(item.value)}</div>`
            ].join('');
          }
        },
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '41%',
            style: { text: String(total), fontSize: 22, fontWeight: 800, fill: '#111827', fontFamily: CHART_FONT.fontFamily }
          },
          {
            type: 'text',
            left: 'center',
            top: '55%',
            style: { text: 'Quotations', fontSize: 10, fill: '#6B7280', fontFamily: CHART_FONT.fontFamily }
          }
        ],
        series: [{
          type: 'pie',
          radius: ['52%', '82%'],
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { opacity: 0.85, borderColor: '#ffffff', borderWidth: 2 },
          emphasis: {
            scale: true,
            scaleSize: 4,
            itemStyle: {
              opacity: 1,
              borderWidth: 3,
              shadowBlur: 6,
              shadowOffsetY: 3,
              shadowColor: 'rgba(0,0,0,0.2)'
            }
          },
          data: data.map((item) => ({
            value: item.count,
            name: item.label,
            itemStyle: { color: (STAGE_COLORS[item.stage] || {}).dot || DEFAULT_DOT }
          }))
        }]
      };

  return <ReactECharts option={option} style={{ height: 190, width: '100%' }} notMerge />;
};

DonutSlice.propTypes = { data: PropTypes.array };

const QuotationStagesChart = ({ data, loading, error, onDrillDown }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading quotation data</p>
    </Card>
  );

  const items = data || [];
  const total = items.reduce((s, d) => s + d.count, 0);

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-amber-50 tw-via-white tw-to-green-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200">
        <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
          <FiFileText className="tw-text-amber-600" size={18} />
          Quotation Stages
        </h3>
        <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Breakdown by quotation pipeline stage</p>
      </div>

      <div className="tw-p-5">
        <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-5 tw-items-center">
          <div className="tw-w-44 tw-flex-shrink-0">
            <DonutSlice data={items} />
          </div>
          <div className="tw-flex-1 tw-space-y-3 tw-w-full">
            {items.map((item) => {
              const cfg = STAGE_COLORS[item.stage] || {};
              const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
              return (
                <div
                  key={item.stage}
                  className={`tw-rounded-lg tw-p-3 tw-transition-all${onDrillDown && item.count > 0 ? ' tw-cursor-pointer hover:tw-shadow-md hover:tw-opacity-90' : ''}`}
                  style={{ backgroundColor: cfg.bg || '#F9FAFB' }}
                  onClick={() => onDrillDown && item.count > 0 && onDrillDown(
                    `${item.label} (${item.count})`,
                    { quotation_stage: item.stage }
                  )}
                >
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <div className="tw-flex tw-items-start tw-gap-2 tw-min-w-0">
                      <span className="tw-w-2.5 tw-h-2.5 tw-rounded-xl tw-mt-1 tw-flex-shrink-0" style={{ backgroundColor: cfg.dot }} />
                      <div className="tw-min-w-0">
                        <span className="tw-text-xs tw-font-semibold tw-block" style={{ color: cfg.text }}>{item.label}</span>
                        {item.description && (
                          <span className="tw-text-[11px] tw-leading-snug tw-block tw-mt-0.5" style={{ color: cfg.text, opacity: 0.65 }}>{item.description}</span>
                        )}
                      </div>
                    </div>
                    <span className="tw-text-lg tw-font-bold tw-flex-shrink-0" style={{ color: cfg.text }}>{item.count}</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-between tw-mt-1.5">
                    <span className="tw-text-xs" style={{ color: cfg.text, opacity: 0.7 }}>{pct}% · {fmtM(item.value)}</span>
                    {item.weight > 0 && (
                      <span className="tw-text-xs tw-font-semibold tw-text-cyan-600">{Number(item.weight).toLocaleString('en-US', { maximumFractionDigits: 0 })} MT</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

QuotationStagesChart.propTypes = { data: PropTypes.array, loading: PropTypes.bool, error: PropTypes.any, onDrillDown: PropTypes.func };
QuotationStagesChart.defaultProps = { data: [], loading: false, error: null, onDrillDown: null };

export default QuotationStagesChart;
